import { H1, H3, H4, H5 } from 'components/typography/headings';
import { P } from 'components/typography/text';
import { Container } from 'react-bootstrap';
import styled, { css, keyframes } from 'styled-components';
import { breakpointDown } from 'styles/breakpoints';
import WhiteDownloadIcon from '../../../assets/images/white-download-icon.svg';

const SpinAnimation = keyframes`
    from { transform: scale(1) rotate(0deg); }
    to { transform: scale(1) rotate(360deg); }
`;

// Header Sticky Bar Components
export const HeaderStickyBar = styled.div<{ $isSticky?: boolean }>`
    margin-bottom: ${({ theme }) => theme.space[5]};
    display: flex;
    align-items: center;
    min-height: 71px;
    background-color: ${({ theme }) => theme.colors.green};
    color: ${({ theme }) => theme.colors.white};

    ${breakpointDown.md`
       min-height: 160px;
       text-align: center;
    `}

    ${({ $isSticky }) =>
        $isSticky &&
        `
        width: 100%;
        position: fixed;
        top: 0;
        z-index: 100;
        filter: drop-shadow(0px 5px 10px rgba(0, 0, 0, 0.1));
    `}
`;

export const HeaderStickyBarContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;

    ${breakpointDown.sm`
        flex-direction: column;
    `}
`;

export const HeaderStickyBarTitle = styled(H3)`
    margin: 0;
    color: ${({ theme }) => theme.colors.white}!important;
`;

export const HeaderStickyBarDownloadPdf = styled.div`
    display: flex;
    gap: 20px;
    align-items: center;

    ${breakpointDown.xs`
        display: block;
    `}
`;

// Tooltip Components
export const TooltipHelpBox = styled.div`
    display: none;
    width: 351px;
    padding: 20px;
    position: absolute;
    top: 30px;
    left: -50px;
    z-index: 99;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.colors.aubergine};
`;

export const Tooltip = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    position: relative;

    &:hover ${TooltipHelpBox} {
        display: block;
    }
`;

export const TooltipIcon = styled.img``;

export const TooltipText = styled.span`
    text-decoration: underline;
    cursor: pointer;

    ${breakpointDown.xs`
        margin: 10px 0;
    `}
`;

// PDF Button Components
export const PdfButtonWrapper = styled.div<{ $isDownloading?: boolean }>`
    display: flex;
    align-items: center;
    background-color: transparent;
    border-radius: 4px;
    cursor: pointer;
    text-decoration: none;
    gap: 14px;
    padding: 6px 12px;
    border: 2px solid ${({ theme }) => theme.colors.white};
    font-family: 'PhoenixSansMediumWeb';
    color: ${({ theme }) => theme.colors.white};
    text-align: left;
    transition: border-bottom-right-radius 180ms ease-in-out;
    width: auto;

    &:focus,
    &:hover {
        border-bottom-right-radius: 22px;
    }

    &:before {
        content: '';
        display: inline-block;
        // prettier-ignore
        background: url("${WhiteDownloadIcon}") no-repeat center center;
        height: 32px;
        width: 32px;
    }

    ${({ $isDownloading }) =>
        $isDownloading &&
        css`
            opacity: 0.6;
            pointer-events: none;
            padding: 10px 12px;

            &::before {
                background: none;
                opacity: 1;
                content: '\\f021';
                font-family: 'Font Awesome 6 Free';
                animation: ${SpinAnimation} 1s infinite linear;
                font-weight: 900;
                height: auto;
                width: auto;
            }
        `}

    a {
        display: flex;
        align-items: center;
        gap: 14px;
        color: ${({ theme }) => theme.colors.white};
        text-decoration: none;
        font-family: 'PhoenixSansMediumWeb';
    }
`;

// Results Components
export const ResultsContainer = styled.div<{ $isSticky?: boolean }>`
    ${({ $isSticky }) =>
        $isSticky &&
        `
        margin-top: 70px;
    `}

    & > div {
        ${breakpointDown.md`
            padding: 30px;
        `}
    }
`;

export const ResultsTop = styled.div`
    margin-bottom: ${({ theme }) => theme.space[7]};
    text-align: center;

    ${breakpointDown.md`
        margin-bottom: 0;
    `}
`;

export const ResultsTitle = styled(H1)`
    font-family: 'PhoenixSansMediumWeb';
    margin-top: 0;
    font-size: 40px;
    line-height: ${({ theme }) => theme.fontSizes[12]}!important;
`;

export const ResultsSubtitle = styled(H3)`
    font-family: 'PhoenixSansMediumWeb';
`;

export const ScoreColor = styled.span`
    color: ${({ theme }) => theme.colors.green};
`;

export const ResultsSection = styled.div<{ $variant?: 'wellbeing' | 'worklife' | 'wealth' }>`
    height: 380px;
    border: 1px solid ${({ theme }) => theme.colors.white};
    border-radius: 5px;
    padding: 30px 26px;
    margin-bottom: 24px;
    text-align: center;
    box-shadow: 0px 6px 10px 5px rgba(0, 0, 0, 0.07);
    position: relative;

    ${breakpointDown.md`
        margin-top: 30px;
        height: auto;
    `};

    ${({ $variant, theme }) =>
        $variant === 'wellbeing' &&
        `
        border-color: ${theme.colors.purple};
    `}

    ${({ $variant, theme }) =>
        $variant === 'worklife' &&
        `
        border-color: ${theme.colors.teal};
    `}

    ${({ $variant, theme }) =>
        $variant === 'wealth' &&
        `
        border-color: ${theme.colors.brightGreen};
    `}

    &:last-child {
        margin-bottom: 0;
    }
`;

export const ResultsSectionImg = styled.img`
    position: absolute;
    top: 85px;
    left: 50%;
    width: 25px;
    height: 25px;
    transform: translateX(-50%);
    z-index: 20;
`;

export const ResultsSectionTitle = styled(H5)`
    font-family: 'PhoenixSansBoldWeb';
    margin: 0;
    margin-bottom: 11px;
    font-size: ${({ theme }) => theme.fontSizes[8]}!important;
    line-height: ${({ theme }) => theme.fontSizes[8]}!important;

    ${breakpointDown.md`
        margin-top: 30px;
    `};
`;

export const ResultsSectionScore = styled(P)`
    font-family: 'PhoenixSansMediumWeb';
    margin-bottom: 11px !important;
`;

export const Strong = styled.span`
    font-family: 'PhoenixSansBoldWeb';
`;

export const ResultsImprove = styled.div`
    margin-top: 40px;
    background-color: ${({ theme }) => theme.colors.aubergine};
    color: ${({ theme }) => theme.colors.white};
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 20px;

    ${breakpointDown.md`
        display: block;
    `}
`;

export const ResultsImproveTitle = styled(H4)`
    margin-top: 0;
    width: 550px;
    color: ${({ theme }) => theme.colors.white}!important;
`;

export const ResultsImproveText = styled.div`
    span {
        margin: 0 5px;

        a {
            color: ${({ theme }) => theme.colors.ember};
        }
    }
`;

export const ResultsLink = styled.a`
    font-family: 'PhoenixSansMediumWeb';
    color: ${({ theme }) => theme.colors.charcoal};
    text-decoration: none;
    font-size: ${({ theme }) => theme.fontSizes[5]}!important;
    line-height: ${({ theme }) => theme.fontSizes[8]}!important;
`;

export const ResultsReminder = styled.div`
    max-width: 728px;
    margin: 0 auto;

    &__row {
        margin-top: ${({ theme }) => theme.space[5]};
    }
`;

// Legacy Results Style wrapper for backward compatibility
export const ResultsStyle = styled.div``;

export const RuiContainer = styled(Container)`
    background-color: ${({ theme }) => theme.colors.white};
    max-width: 1200px;
    padding: 40px 65px;
`;
